import { FC, useState, useEffect } from "react";
import moment from "moment";
import appState from "../../state/AppStateContainer";
import { NotificationManager } from 'react-notifications';
import Modal from "../../components/shared/Modal/Modal";
import Field from "../../components/shared/Field/Field";
import DatePicker from "../../components/shared/DatePicker/DatePicker";
import EmailTemplate from "../../components/shared/EmailTemplate/EmailTemplate";
import { useTranslation } from "react-i18next";

type Props = {
  show: boolean,
  setShow: (value: boolean) => void,
  defaultDate: Date,
  onSave: ({ date, emailText }: { date: Date, emailText: string }) => Promise<void>,
  data: { areaName: string, projectName: string, version: number },
}

const DateExtensionModal: FC<Props> = ({
  show,
  setShow,
  defaultDate,
  onSave,
  data
}) => {
  const [t] = useTranslation('projects', { keyPrefix: 'dateExtension' });
  const [date, setDate] = useState(defaultDate || new Date());
  const [emailText, setEmailText] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // The modal stays mounted (only `show` toggles), so re-seed the picker with the default date
  // (today) every time it is opened rather than keeping the previously picked value.
  useEffect(() => {
    if (show) setDate(defaultDate || new Date());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const { areaName, projectName, version } = data;
  const { name, phone } = appState.state.token;
  const organizationName = appState.getCurrentOrgName();

  const onSubmit = async () => {
    setIsSaving(true);
    try {
      await onSave({ date: moment(date).endOf('day').utc().toDate(), emailText });
      NotificationManager.success(
        t('notification.success', 'Uppdaterade utgångsdatumet'),
        t('notification.successTitle', 'Sparat'),
      );
    } catch (e) {
      console.error('Failed to change the bid deadline', e);
      NotificationManager.error(
        t('notification.error', 'Det gick inte att ändra anbudstiden, försök igen'),
        t('notification.errorTitle', 'Fel'),
      );
    }
    setIsSaving(false);
  };

  return (
    <Modal
      title={t('heading', 'Ändra anbudstid')}
      show={show}
      setShow={setShow}
      buttonInfo={{ label: 'Skicka', action: onSubmit, loaderShow: isSaving }}
      hasCloseButton
    >
      <div className='dateExtensionModal'>
        <p>{t('intro', 'Välj nytt datum och skriv ett meddelande nedan för att ändra utgångsdatum. Följande e-postmeddelande skickas till leverantörens kontaktpersoner:')}<br/><br/></p>

        <EmailTemplate>
          <p>{t('emailTemplate.greeting', 'Hej <Förnamn>,')}</p>
          <p>{t(
            'emailTemplate.intro',
            '{{name}} på {{organizationName}} har ändrat anbudstiden för {{areaName}} till projektet {{projectName}}.',
            { name, organizationName, areaName, projectName}
          )}</p>

          <a style={{ alignSelf: 'center', backgroundColor: 'var(--orange-500)', padding: '16px', borderRadius: '6px', cursor: 'not-allowed', color: 'var(--white)', textDecoration: 'none', }}>
            {t('emailTemplate.button', 'Öppna anbudssidan i Accurator')}
          </a>
          <div>
            <p><strong>{t('emailTemplate.info.title', 'Anbudsinformation:')}</strong></p>
            <ul style={{ paddingLeft: '32px', margin: 0 }}>
              <li><p>{t('emailTemplate.info.label.area', 'Kategori')}: {areaName}</p></li>
              <li><p>{t('emailTemplate.info.label.version', 'Version')}: {version + 1}</p></li>
              <li>
                <p className='dateExtensionModalDateContainer'>
                  <span>{t('emailTemplate.info.label.date', 'Utgångsdatum')}:</span>
                  <DatePicker
                    minDate={new Date()}
                    selected={date}
                    onChange={setDate}
                  />
                </p>
              </li>
            </ul>
          </div>

          <div>
            <p><strong>{t('emailTemplate.sender.title', 'Avsändare:')}</strong></p>
            <ul style={{ paddingLeft: '32px', margin: 0}}>
              <li><p>{t('emailTemplate.sender.label.name', 'Namn')}: {name}</p></li>
              {phone ? <li><p>{t('emailTemplate.sender.label.phone', 'Telefonnummer')}: {phone}</p></li> : ''}
              <li><p>{t('emailTemplate.sender.label.organization', 'Företag')}: {organizationName}</p></li>
            </ul>
          </div>

          <div>
            <p><strong style={{ display: 'block', paddingBottom: '5px' }}>{t('emailTemplate.message.title', 'Meddelande från avsändaren:')}</strong></p>
            <Field multiline value={emailText} onChange={setEmailText} /><br/>
          </div>
        </EmailTemplate>
      </div>
    </Modal>
  )
}

export default DateExtensionModal;
